import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import FolderOpenRounded from "@mui/icons-material/FolderOpenRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { NavLink } from "react-router-dom";
import useContratosAtivos from "../hooks/useContratosAtivos";
import taskAgentService from "../features/taskAgent/services/taskAgent.service";

const TASK_AGENT_PRODUCT_ID = 7;

const TaskAgentInicio = () => {
  const { contratosAtivos, loading: loadingContrato } = useContratosAtivos();
  const [totalPastasComTarefas, setTotalPastasComTarefas] = useState(0);
  const [loadingPastas, setLoadingPastas] = useState(true);
  const [erroContrato, setErroContrato] = useState("");

  const contratoTaskAgent = useMemo(
    () => contratosAtivos.find((contrato) => Number(contrato?.idProduto) === TASK_AGENT_PRODUCT_ID) || null,
    [contratosAtivos]
  );

  const limiteProcessos = Number(contratoTaskAgent?.limite ?? 0);
  const isExcedente = limiteProcessos > 0 && totalPastasComTarefas > limiteProcessos;
  const percentualConsumo = limiteProcessos > 0 ? (totalPastasComTarefas / limiteProcessos) * 100 : 0;
  const percentualConsumoLimitado = Math.min(percentualConsumo, 100);

  useEffect(() => {
    let active = true;

    const loadPastas = async () => {
      setLoadingPastas(true);
      setErroContrato("");

      try {
        const pastas = await taskAgentService.listPastas();
        if (!active) return;
        const totalComTarefas = Array.isArray(pastas)
          ? pastas.filter((pasta) => Number(pasta?.totalTarefas ?? 0) > 0).length
          : 0;
        setTotalPastasComTarefas(totalComTarefas);
      } catch (error) {
        if (!active) return;
        setErroContrato("Não foi possível carregar as informações do contrato do TaskAgent.");
      } finally {
        if (active) setLoadingPastas(false);
      }
    };

    loadPastas();

    return () => {
      active = false;
    };
  }, []);

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: { xs: 3, md: 4 },
          bgcolor: "background.paper",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: {
            xs: "0 2px 10px rgba(0,0,0,0.08)",
            md: "0 4px 20px rgba(0,0,0,0.08)",
          },
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={{ xs: 2.5, lg: 3 }}
            alignItems={{ xs: "center", lg: "center" }}
            justifyContent="space-between"
            sx={{ maxWidth: 1120, mx: "auto" }}
          >
            <Stack
              spacing={{ xs: 2, md: 2.25 }}
              alignItems="center"
              textAlign="center"
              sx={{ flex: 1, maxWidth: { xs: 860, lg: 680 } }}
            >
              <Stack direction="row" spacing={1.1} alignItems="center" justifyContent="center">
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 2.5,
                    display: "grid",
                    placeItems: "center",
                    position: "relative",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(14, 21, 38, 0.94)" : "rgba(248, 250, 255, 0.98)",
                    border: "1px solid rgba(102, 126, 234, 0.24)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 10px 24px rgba(16,24,40,0.08)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: 2.5,
                      padding: "1px",
                      background: "linear-gradient(135deg, rgba(102,126,234,0.65), rgba(118,75,162,0.45))",
                      WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      pointerEvents: "none",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 10,
                      left: 10,
                      width: 18,
                      height: 3,
                      borderRadius: 999,
                      background: "linear-gradient(90deg, #667eea, #764ba2)",
                      pointerEvents: "none",
                    },
                  }}
                >
                  <ChecklistOutlined sx={{ fontSize: 28, color: "primary.main" }} />
                </Box>
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{
                    letterSpacing: -0.8,
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "2rem", md: "2.4rem" },
                  }}
                >
                  TaskAgent
                </Typography>
              </Stack>

              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  letterSpacing: -0.7,
                  lineHeight: 1.08,
                  fontSize: { xs: "1.35rem", md: "1.95rem" },
                  color: "text.primary",
                  maxWidth: 620,
                }}
              >
                Organize tarefas, fluxos e prioridades em um só lugar.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.98rem", md: "1.02rem" },
                  lineHeight: 1.55,
                  maxWidth: 720,
                }}
              >
                O TaskAgent é o módulo da Aipim.tech para estruturar a operação do time com pastas, etapas,
                responsáveis, prazos e acompanhamento claro da execução.
              </Typography>

              <Stack
                direction="row"
                spacing={1.2}
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
              >
                <Chip
                  label="Pastas e subtarefas"
                  size="small"
                  sx={{
                    bgcolor: "#e8f5e8",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                />
                <Chip
                  label="Kanban por etapa"
                  size="small"
                  sx={{
                    bgcolor: "#eef2ff",
                    color: "#4355b9",
                    fontWeight: 500,
                  }}
                />
                <Chip
                  label="Prioridades e responsaveis"
                  size="small"
                  sx={{
                    bgcolor: "#f5f5f5",
                    color: "#616161",
                    fontWeight: 500,
                  }}
                />
              </Stack>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                justifyContent="center"
                alignItems="center"
                sx={{ width: "100%" }}
              >
                <Button
                  component={NavLink}
                  to="/task-agent/pastas"
                  variant="contained"
                  startIcon={<FolderOpenRounded />}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 3,
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, #7c8cff 0%, #9b68d8 100%)"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 14px 28px rgba(124, 140, 255, 0.28)"
                        : "0 12px 26px rgba(102, 126, 234, 0.26)",
                    "&:hover": {
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, #8a98ff 0%, #a874e0 100%)"
                          : "linear-gradient(135deg, #5d70e6 0%, #6e4299 100%)",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 18px 34px rgba(124, 140, 255, 0.34)"
                          : "0 16px 30px rgba(102, 126, 234, 0.3)",
                    },
                  }}
                >
                  Abrir TaskAgent
                </Button>
                <Button
                  component={NavLink}
                  to="/task-agent/ajuda"
                  variant="outlined"
                  startIcon={<HelpOutlineRounded />}
                  sx={{
                    px: 2.3,
                    py: 1,
                    borderRadius: 3,
                    color: (theme) => (theme.palette.mode === "dark" ? "#eef2ff" : "text.primary"),
                    borderColor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(124, 140, 255, 0.55)" : "rgba(102, 126, 234, 0.24)",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(124, 140, 255, 0.18)" : "rgba(248,250,255,0.9)",
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark" ? "inset 0 1px 0 rgba(255,255,255,0.05)" : "none",
                    "&:hover": {
                      borderColor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(124, 140, 255, 0.8)" : "rgba(102, 126, 234, 0.38)",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark" ? "rgba(124, 140, 255, 0.26)" : "rgba(238,242,255,0.95)",
                    },
                  }}
                >
                  Saiba mais
                </Button>
              </Stack>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 310,
                borderRadius: 3,
                p: { xs: 1.75, sm: 2 },
                textAlign: "center",
                border: "2px solid #4caf50",
                bgcolor: "background.paper",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: "linear-gradient(90deg, #4caf50, #81c784)",
                },
              }}
            >
              <Stack spacing={1.5} alignItems="center">
                <Box sx={{ position: "absolute", top: 16, right: 16 }}>
                  <Chip
                    icon={<CheckCircleRounded sx={{ fontSize: "16px !important" }} />}
                    label="Ativo"
                    size="small"
                    sx={{
                      bgcolor: "#e8f5e8",
                      color: "#2e7d32",
                      fontWeight: 700,
                      "& .MuiChip-icon": { color: "#2e7d32" },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: 78,
                    height: 78,
                    borderRadius: 2.5,
                    display: "grid",
                    placeItems: "center",
                    position: "relative",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "rgba(14, 21, 38, 0.94)" : "rgba(248, 250, 255, 0.98)",
                    border: "1px solid rgba(102, 126, 234, 0.30)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 10px 24px rgba(16,24,40,0.08)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      borderRadius: 2.5,
                      padding: "1px",
                      background: "linear-gradient(135deg, rgba(102,126,234,0.65), rgba(118,75,162,0.45))",
                      WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      pointerEvents: "none",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: 10,
                      left: 10,
                      width: 18,
                      height: 3,
                      borderRadius: 999,
                      background: "linear-gradient(90deg, #667eea, #764ba2)",
                      pointerEvents: "none",
                    },
                  }}
                >
                  <ChecklistOutlined sx={{ fontSize: 38, color: "primary.main" }} />
                </Box>

                <Typography variant="h5" fontWeight={800} sx={{ color: "text.primary", fontSize: { xs: "1.45rem", md: "1.6rem" } }}>
                  Aipim TaskAgent
                </Typography>

                {erroContrato ? (
                  <Alert severity="warning" sx={{ borderRadius: 2.5, width: "100%" }}>
                    {erroContrato}
                  </Alert>
                ) : loadingContrato || loadingPastas ? (
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Carregando informações do contrato...
                    </Typography>
                  </Stack>
                ) : (
                  <Stack
                    sx={{
                      width: "100%",
                      pt: 0.5,
                      borderTop: "1px solid #e6edf5",
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                      {contratoTaskAgent?.descricaoPlano || contratoTaskAgent?.nomePlano || "Plano não identificado"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#607d8b", mt: 1.25 }}>
                      Processos contratados:{" "}
                      <Box component="span" sx={{ color: "text.primary", fontWeight: 800 }}>
                        {limiteProcessos > 0 ? limiteProcessos : "Sem limite informado"}
                      </Box>
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#607d8b", mt: 0.5 }}>
                      Processos utilizados:{" "}
                      <Box component="span" sx={{ color: isExcedente ? "error.main" : "text.primary", fontWeight: 800 }}>
                        {totalPastasComTarefas}
                      </Box>
                    </Typography>
                    {limiteProcessos > 0 && (
                      <Box sx={{ mt: 1.25 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#607d8b",
                            fontWeight: 500,
                            display: "block",
                            mb: 0.75,
                          }}
                        >
                          Consumo:{" "}
                          <Box component="span" sx={{ color: isExcedente ? "error.main" : "inherit", fontWeight: 700 }}>
                            {totalPastasComTarefas}
                          </Box>{" "}
                          / {limiteProcessos}
                        </Typography>
                        <Box
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            bgcolor: "#eceff1",
                            overflow: "hidden",
                          }}
                          aria-label={`Consumo ${Math.round(percentualConsumo)}%`}
                        >
                          <Box
                            sx={{
                              width: `${percentualConsumoLimitado}%`,
                              height: "100%",
                              background: isExcedente
                                ? "linear-gradient(135deg, #ef5350 0%, #e53935 100%)"
                                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              transition: "width 0.3s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
};

export default TaskAgentInicio;
